<template>
  <div class="view-product-plans">
    <step-title
      title="Precificação e planos"
      description="Visualize e configure os preços do seu produto"
    />
    <action-dispatcher
      action="product/getProduct"
      :parameters="$route.params.id"
      @success="loaded"
    >
      <template v-if="product">
        <div class="row my-6">
          <div class="col-12 col-md-4">
            <div class="info">
              <h3>
                Planos cadastrados
              </h3>
              <h3>
                {{ pagination.count || 0 }}
              </h3>
            </div>
          </div>
          <div class="col-12 col-md-4 mt-4 mt-md-0">
            <div class="info">
              <h3>Garantia do produto</h3>
              <h3>
                {{ product.warranty }} dia{{ product.warranty == 1 ? "" : "s" }}
              </h3>
            </div>
          </div>
          <div class="col-12 col-md-4 mt-4 mt-md-0">
            <div class="info">
              <h3>Status de vendas</h3>
              <bp-switch
                v-model="isActive"
                @change="changeStatus"
                style="min-height: unset;"
                label="Ativas"
                :disabled="
                  ['in_review', 'reject', 'draft'].includes(product.status)
                "
              />
            </div>
          </div>
        </div>

        <hr class="mb-6 " />

        <div
          class="row d-none d-md-flex justify-content-between align-items-center mb-4"
        >
          <div class="col-2">
            <div class="d-flex align-items-center">
              <span class="text-neutral-400 fs-14">
                Total: {{ pagination.count }}
              </span>
              <bp-button
                size="md"
                color="neutral"
                variant="glyph"
                @click="refresh"
              >
                <i class="fa-light fa-refresh" />
              </bp-button>
            </div>
          </div>
          <div class="col-6 col-lg-5">
            <div class="d-flex gap-3">
              <bp-input
                v-model="searchInput"
                size="md"
                placeholder="Buscar..."
                :delay="600"
                @change="search"
              />
              <bp-button
                @click="newPlan"
                size="md"
                label="Novo plano"
                icon="fa-light fa-plus"
              />
            </div>
          </div>
        </div>

        <div class="d-md-none mb-4">
          <bp-input
            v-model="searchInput"
            class="my-4"
            size="md"
            placeholder="Buscar..."
            :delay="600"
            @change="search"
          />

          <bp-button
            block
            @click="newPlan"
            size="md"
            label="Nova oferta"
            icon="fa-light fa-plus"
          />

          <div class="d-flex align-items-center my-4">
            <span class="text-neutral-400 fs-14">
              Total: {{ pagination.count }}
            </span>
            <bp-button
              size="md"
              color="neutral"
              variant="glyph"
              @click="refresh"
            >
              <i class="fa-light fa-refresh" />
            </bp-button>
          </div>
        </div>

        <div v-if="loading" class="text-center">
          <b-spinner variant="primary" />
        </div>

        <bp-data-table v-else :columns="columns" :data="plans">
          <template #status="{value}">
            <div>
              <bp-tag
                :text="formatStatusText(value)"
                :color="formatStatusColor(value)"
              />
            </div>
          </template>
        </bp-data-table>

        <div class="d-flex justify-content-center justify-content-lg-end mt-5">
          <bp-paginator
            v-model="currentPage"
            v-show="plans.length && !loading"
            :pagination="pagination"
            @change-page="onPaginationUpdate"
          />
        </div>
      </template>
    </action-dispatcher>
  </div>
</template>

<script>
import { PERIODICITY_NAME_MAP } from "@/constants/date.js";
import { clearObject, formatMoney } from "@/functions/helpers";
import ProductActionsMixin from "@/mixins/productActions.js";
import copy from "copy-text-to-clipboard";
import { mapState } from "vuex";

export default {
  mixins: [ProductActionsMixin],
  data() {
    return {
      currentPage: 1,
      searchInput: "",
      loading: false,
      isActive: false,
      params: {
        product_id: this.$route.params.id,
        search: "",
        offset: 0
      }
    };
  },
  computed: {
    columns() {
      return [
        { name: "Nome", field: "name", size: "1fr" },
        {
          name: "Periodicidade",
          field: "periodicity",
          size: "1fr",
          transform: v => PERIODICITY_NAME_MAP[v]
        },
        {
          name: "Descrição",
          field: "description",
          size: "1fr",
          limit: 28
        },
        {
          name: "Preço",
          field: "price",
          size: "1fr",
          transform: v => `R$ ${formatMoney(v)}`
        },
        {
          name: "Cobrança",
          field: "num_max_payments",
          size: "1fr",
          transform: (v, item) =>
            item.num_max_payments
              ? `${item.num_max_payments} recorrência${
                  item.num_max_payments == 1 ? "" : "s"
                }`
              : "Até o cliente cancelar"
        },
        {
          name: "Teste grátis",
          field: "test_days",
          size: "1fr",
          transform: v => `${v || 0} dia${v == 1 ? "" : "s"}`
        },
        {
          name: "SCK",
          field: "scr",
          size: "1fr"
        },
        { name: "Status", field: "status", size: "1fr" },
        {
          name: "",
          type: "menu",
          options: this.itemActions
        }
      ];
    },
    ...mapState("product", ["product"]),
    ...mapState("plan", ["plans", "pagination"])
  },
  methods: {
    formatStatusText(status) {
      const statusMap = {
        active: "Ativa",
        inactive: "Inativa"
      };
      return statusMap[status] || "Inativa";
    },
    formatStatusColor(status) {
      const statusMap = {
        active: "positive",
        inactive: "negative"
      };
      return statusMap[status];
    },
    changePlanStatus(item) {
      this.$store
        .dispatch("plan/put", {
          id: item.id,
          status: item.status === "active" ? "inactive" : "active"
        })
        .then(() => {
          this.$message.success("Status do plano alterado com sucesso.");
          this.dispatch();
        })
        .catch(() => {
          this.$message.error("Erro ao alterar status do plano.");
        });
    },
    newPlan() {
      this.$router.push({
        name: "product-plans-new",
        params: { id: this.$route.params.id }
      });
    },
    changeStatus() {
      this.$store
        .dispatch("product/put", {
          id: this.$route.params.id,
          status: this.isActive ? "active" : "inactive"
        })
        .then(() => {
          this.$message.success(
            `Produto ${this.isActive ? "ativado" : "desativado"} com sucesso!`
          );
        })
        .catch(() => {
          this.$message.error("Erro ao atualizar status de vendas.");
          this.isActive = !this.isActive;
        });
    },
    loaded() {
      this.isActive = this.product.status === "active";
    },
    itemActions(item) {
      const actions = [];

      actions.push({
        label: "Editar",
        icon: "fa-light fa-pen",
        click: this.edit
      });

      actions.push({
        label: "Copiar link do plano",
        icon: "fa-light fa-link",
        click: this.copy
      });

      actions.push({
        label: "Duplicar plano",
        icon: "fa-light fa-clone",
        click: this.duplicate
      });

      actions.push({
        label: `${item.status === "active" ? "Desativar" : "Ativar"} plano`,
        class:
          item.status === "active" ? "text-negative-600" : "text-positive-600",
        icon: "fa-light fa-power-off",
        click: this.changePlanStatus
      });

      if (this.plans.length > 1) {
        actions.push({
          label: "Excluir plano",
          icon: "fa-light fa-trash",
          class: "text-negative-600",
          dialog: {
            text:
              "Tem certeza que deseja excluir este plano? Seus clientes não poderão mais encontrar e comprar este plano.",
            variant: "negative",
            icon: "fa-light fa-trash",
            secondaryAction: i => this.deletePlan(i.id),
            buttonText: "Cancelar",
            secondaryButtonText: "Excluir plano"
          }
        });
      }
      return actions;
    },
    edit(item) {
      this.$router.push({
        name: "product-plans-edit",
        params: { id: this.$route.params.id, plan_id: item.id }
      });
    },
    copy(item) {
      copy(item.url);
      this.$message.success("Link copiado");
    },
    duplicate(item) {
      this.$store
        .dispatch("plan/duplicate", item)
        .then(() => {
          this.refresh();
          this.$message.success("Plano duplicado.");
        })
        .catch(() => this.$message.error("Erro ao duplicar plano."));
    },
    deletePlan(id) {
      this.$store
        .dispatch("plan/delete", id)
        .then(() => {
          this.$message.success("Plano deletado");
          this.refresh();
        })
        .catch(() => this.$message.error("Erro ao deletar plano"));
    },
    refresh() {
      this.currentPage = 1;
      this.dispatch();
    },
    dispatch() {
      this.loading = true;
      this.$store
        .dispatch("plan/get", clearObject(this.params))
        .catch(() => {
          this.$message.error("Não foi possível carregar os planos");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onPaginationUpdate(offset) {
      this.params.offset = offset;
      this.dispatch();
    },
    search(search) {
      this.params.search = search;
      this.refresh();
    }
  },
  mounted() {
    this.dispatch();
  }
};
</script>

<style lang="scss" scoped>
.view-product-plans {
  .info {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border: 1px solid $neutral-100;
    padding: 16px;
    gap: 12px;
    height: 100%;
  }
}
</style>
